import React, { useState, useRef, useEffect } from 'react';
import ContainerGridItem from './ContainerGridItem';
import NewContainer from './NewContainer';
import {
  useLoaderData,
  Link,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { sortByAlpha } from '../../helpers/sorters';
import Icon from '../common/Icon';
import QuickFilter from '../common/QuickFilter';

const ContainerIndex = () => {
  const { data } = useLoaderData();
  const location = useLocation();
  const nav = useNavigate();
  const adminUser = data.current_user.admin;
  const [containers, setContainers] = useState(
    sortByAlpha(data.containers, 'name'),
  );
  const containersRef = useRef();
  const [activeInactiveForms, setActiveInactiveForms] = useState(
    data.active_inactive_forms,
  );
  const [showArchived, setShowArchived] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter] = useState('');
  const nonArchivedContainers = containers.filter((c) => !c.archived);
  const archivedContainers = containers.filter((c) => c.archived);
  const transitionStyle = {
    transition: 'max-height 0.5s ease-in-out',
    overflowY: 'clip',
  };

  useEffect(() => {
    if (location.hash) {
      if (location.hash.substring(1) === '/containers') {
        history.replaceState(null, '', location.pathname);
      } else {
        nav(location.pathname + location.hash.substring(1), { replace: true });
      }
    }
  }, [location]);

  useEffect(() => {
    determineHeight('archived-containers', showArchived);
    determineHeight('inactive-containers', showInactive);
  }, [showArchived, showInactive, containers]);

  useEffect(() => {
    if (!containersRef.current) containersRef.current = data.containers;
    let showContainers = containersRef.current;
    if (filter) {
      showContainers = showContainers.filter((c) => {
        let values = [c.name?.toLowerCase(), c.display_name?.toLowerCase()];
        return values.some((names) => names.includes(filter.toLowerCase()));
      });
      setContainers(sortByAlpha(showContainers, 'name'));
      if (!showInactive) setShowInactive(true);
      if (!showArchived) setShowArchived(true);
    } else {
      if (showInactive) setShowInactive(false);
      if (showArchived) setShowArchived(false);
      setContainers(sortByAlpha(showContainers, 'name'));
    }
  }, [filter]);

  const determineHeight = (elementId, showBool) => {
    let element = document.getElementById(elementId);
    if (element) {
      let height = element.scrollHeight;
      element.style.maxHeight = showBool ? `${height}px` : '0px';
    }
  };

  const updateContainerIndex = (container) => {
    setActiveInactiveForms(() => {
      return {
        ...activeInactiveForms,
        [container.id]: { inactive: 0, active: 0 },
      };
    });
    let updated = containers.concat(container);
    containersRef.current = containersRef.current.concat(container);
    setContainers(sortByAlpha(updated, 'name'));
  };

  const handleFilterChange = (e) => {
    e.preventDefault();
    setFilter(e.target.value);
  };

  return (
    <>
      <div className='container'>
        <header>
          <div className='breadcrumbs'>
            <ol>
              <li className='single-item'>
                <Link to='/manage'>Containers</Link>
              </li>
            </ol>
          </div>
        </header>
        {adminUser && (
          <NewContainer updateContainerIndex={updateContainerIndex} />
        )}
        <section>
          <QuickFilter
            filter={filter}
            clearFilter={() => setFilter('')}
            handleFilterChange={handleFilterChange}
            placeholder={'Container name...'}
          />
          {/* Active */}
          <div className='grid-container'>
            <div className='grid'>
              {nonArchivedContainers.filter((c) => c.active).length ? (
                nonArchivedContainers
                  .filter((c) => c.active)
                  .map((container) => {
                    let active = activeInactiveForms[container.id]['active'];
                    let inactive =
                      activeInactiveForms[container.id]['inactive'];
                    return (
                      <ContainerGridItem
                        container={container}
                        activeCount={active}
                        inactiveCount={inactive}
                        adminUser={adminUser}
                        key={`container-id-${container.id}`}
                        shown={true}
                      />
                    );
                  })
              ) : (
                <p className='none'>No active containers to show</p>
              )}
            </div>
          </div>
          {/* Inactive */}
          <hr />
          <h3
            onClick={() => setShowInactive(!showInactive)}
            className='clickable'
          >
            Inactive Containers{' '}
            <Icon
              title={showInactive ? 'Hide' : 'Show'}
              icon={`angle-${showInactive ? 'up' : 'down'} fa-border`}
            />
          </h3>
          <div
            id='inactive-containers'
            className='grid-container'
            style={transitionStyle}
          >
            <div className='grid' style={{ paddingTop: '0.5rem' }}>
              {nonArchivedContainers.filter((c) => !c.active).length ? (
                nonArchivedContainers
                  .filter((c) => !c.active)
                  .map((container) => {
                    let active = activeInactiveForms[container.id]['active'];
                    let inactive =
                      activeInactiveForms[container.id]['inactive'];
                    return (
                      <ContainerGridItem
                        container={container}
                        activeCount={active}
                        inactiveCount={inactive}
                        adminUser={adminUser}
                        key={`container-id-${container.id}`}
                        shown={showInactive}
                      />
                    );
                  })
              ) : (
                <p className='none'>No inactive containers to show</p>
              )}
            </div>
          </div>
          {/* Archived Containers */}
          <hr />
          <h3
            onClick={() => setShowArchived(!showArchived)}
            className='clickable'
          >
            Archived Containers{' '}
            <Icon
              title={showArchived ? 'Hide' : 'Show'}
              icon={`angle-${showArchived ? 'up' : 'down'} fa-border`}
            />
          </h3>
          <div
            id='archived-containers'
            className='grid-container'
            style={transitionStyle}
          >
            <div className='grid' style={{ paddingTop: '0.5rem' }}>
              {archivedContainers.length ? (
                archivedContainers.map((container) => {
                  let active = activeInactiveForms[container.id]['active'];
                  let inactive = activeInactiveForms[container.id]['inactive'];
                  return (
                    <ContainerGridItem
                      container={container}
                      activeCount={active}
                      inactiveCount={inactive}
                      adminUser={adminUser}
                      key={`container-id-${container.id}`}
                      shown={showArchived}
                    />
                  );
                })
              ) : (
                <p className='none'>No archived containers to show</p>
              )}
            </div>
          </div>
          <hr />
        </section>
      </div>
    </>
  );
};

export default ContainerIndex;
